import { useEffect, useRef, useState } from "react";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";

import strings from "core/strings";
import useNav from "core/hooks/useNav";

import Box from "ui/atom/Box";
import Token from "ui/atom/Token";
import Button from "ui/base/Button";
import Image from "ui/base/Image";
import Content from "ui/Content";

import { useArticle } from "features/article/fabric";


const Title = ({ title, children, subtitle, center = false }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(t => t.breakpoints.down("sm"));

	const style = {
		root: {
			width: "100%",
			alignSelf: center ? "center" : "flex-start",
			textAlign: center ? "center" : "justify"
		},
		subtitle: {
			pl: theme.spacing(1.25),
			borderLeft: center ? "none" : `2px solid ${theme.palette.text.secondary}`,
			color: "text.secondary",
			maxWidth: { xs: "300px", sm: "100%" }
		}
	};

	return (
		<Box.Column sx={style.root}>
			{children || <Typography variant="h1" fontSize={isMobile ? 28 : 32}>{title}</Typography>}
			<Typography variant="body2" fontWeight={center ? 400 : 700} sx={style.subtitle}>{subtitle}</Typography>
		</Box.Column>
	);
};

Title.Campaign = ({ title, subtitle, balance, center = false }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(t => t.breakpoints.down("sm"));

	const style = {
		chip: { borderRadius: "12px", borderColor: "white", fontSize: 12 }
	};
	return (
		<Title subtitle={subtitle} center={center}>
			<Box gap={1} sx={{ alignItems: "center", justifyContent: "flex-start" }}>
				<Image src={title} sx={{ height: isMobile ? 28 : 32 }} />
				<Button.Icon icon="pin" color="tertiary" />
				{balance > 0 && <Chip
					variant="outlined"
					label={<Token icon="campaign-coin" value={isMobile ? balance : strings.pluralize(balance, "free token")} color={theme.palette.coin.main} />} // todo
					sx={style.chip}
				/>}
			</Box>
		</Title>
	);
};

Articles.Campaign = ({ campaigns, loading }) =>	{
	if (loading) return <Articles loading />;
	return campaigns?.map((campaign, k) => {
		const subtitle = campaign.balance > 0 ? campaign.name : `Most Popular ${campaign.partner.name} Articles Unlocked by Subly Users This Week`;

		return <Articles
			id={campaign.code}
			header={<Title.Campaign title={campaign.partner.lightLogo} subtitle={subtitle} balance={campaign.balance} />}
			articles={campaign.content}
			isCampaign
			key={k}
		/>;
	});
};

export default function Articles({ title, subtitle, header, articles, center, loading, id, read = true, ordered = undefined }) {
	const ref = useRef(null);
	const article = useArticle();
	const nav = useNav();
	const theme = useTheme();
	const [navigating, setNavigating] = useState(null);
	const isMobile = useMediaQuery(t => t.breakpoints.down("sm"));

	const hasArticles = articles?.length > 0;
	const list = hasArticles ? articles : [...Array(5).fill(null)];

	const style = {
		root: {
			alignItems: "center",
			textAlign: "center",
			overflow: "visible",
			gap: { xs: 2, sm: 4 },
			"& .swiper-slide": {
				position: "relative",
				maxWidth: isMobile ? "195px" : "270px",
				transition: "all 200ms ease",
				":hover": {
					scale: 1.05,
					zIndex: 2,
					cursor: article.disabled ? "default" : "pointer"
				}
			}
		},
		swiper: {
			width: "100%",
			overflow: "visible",
			padding: theme.spacing(0, 1.25)
		}
	};

	const click = async (e, art) => {
		if (article.disabled) return;

		const tab = e.button === 1 || (e.button === 0 && e.ctrlKey); // middle click or ctrl + left click

		setNavigating(art?.id);
		if (read) await article.unlock(art?.id, tab);
		else tab ? nav.tab(art?.url) : nav.open(art?.url);

		setTimeout(() => {
			setNavigating(null);
			article.store.setDisabled(false);
		}, 1000);
	};

	const preventScroll = (e) => e.button === 1 && e.preventDefault();

	useEffect(() => {
		if (nav.location.hash !== id || !ref.current) return;

		ref.current.scrollIntoView({
			behavior: "smooth",
			block: "center"
		});

	}, [nav.location.hash, id]);

	if (!loading && !hasArticles) return null;
	return (
		<Box.Column ref={ref} sx={style.root}>
			{header || <Title title={title} subtitle={subtitle} center={center} />}
			<Swiper height="max-content" spaceBetween={10} slidesPerView="auto" style={style.swiper}>
				{list.map((a, k) => (
					<SwiperSlide key={k}>
						<Content
							article={a}
							order={ordered && k}
							toggleBookmark={article.toggleBookmark}
							onClick={(e) => click(e, a)}
							onAuxClick={(e) => click(e, a)}
							onMouseDown={preventScroll}
							navigating={navigating === a?.id}
							small={isMobile}
						 />
					</SwiperSlide>
				))}
			</Swiper>
		</Box.Column>
	);
}